import { defineStoredValue, FLAG } from '@utils/storedValue';

/** Exported because it is already the public shape of `getAttribution`,
 * `isQrTraffic` and friends. Callers could not otherwise name what they hold. */
export interface Attribution {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  ref?: string;
  source?: string; // Tracks source=app_share, source=qr_[number], etc.
  ref_user?: string; // User ID who shared (for viral loop tracking)
  ref_event?: string; // Event ID shared (for event viral loop tracking)
  ref_question?: string; // Question ID shared (for question viral loop tracking)
  ref_profile?: string; // Profile ID shared (for profile viral loop tracking)
}

const SESSION_ID_KEY = 'mb_session_id';
const SESSION_LAST_TOUCH_KEY = 'mb_session_last_touch_ms';
// AIDEV-NOTE: session-scoped analytics copy; the durable signup copy lives in @lib/attribution (localStorage, same key name).
const ATTRIBUTION_KEY = 'mb_attribution';

// Once-per-session guards. Read from storage on every call (a handful of times per page),
// not through `cache`: a value storage refuses to keep lives in storedValue's mirror instead.
const qrScanTracked = defineStoredValue({
  storage: 'session',
  key: 'mb_qr_scan_tracked',
  ...FLAG,
});
const referralTracked = defineStoredValue({
  storage: 'session',
  key: 'mb_referral_tracked',
  ...FLAG,
});
const appShareTracked = defineStoredValue({
  storage: 'session',
  key: 'mb_app_share_tracked',
  ...FLAG,
});
const sessionStartedTrackedId = defineStoredValue<string>({
  storage: 'session',
  key: 'mb_session_started_tracked_id',
  parse: raw => raw,
});

const SESSION_TIMEOUT_MS = 30 * 60 * 1000;

const nowMs = () => Date.now();

// AIDEV-NOTE: In-memory cache to reduce synchronous sessionStorage reads
// Values are hydrated on first access, then kept in sync via write-through
const cache: {
  sessionId: string | null;
  lastTouchMs: number | null;
  attribution: Attribution | null;
  hydrated: boolean;
} = {
  sessionId: null,
  lastTouchMs: null,
  attribution: null,
  hydrated: false,
};

/**
 * Safe wrapper for sessionStorage operations
 * Handles QuotaExceededError and other storage failures gracefully
 */
const safeStorageGet = (key: string): string | null => {
  try {
    if (typeof sessionStorage === 'undefined') return null;
    return sessionStorage.getItem(key);
  } catch {
    // Storage unavailable (private browsing, quota exceeded, etc.)
    // Intentionally silent: storage wrappers are called on every page load;
    // logging here would flood the console in private browsing mode.
    return null;
  }
};

const safeStorageSet = (key: string, value: string): boolean => {
  try {
    if (typeof sessionStorage === 'undefined') return false;
    sessionStorage.setItem(key, value);
    return true;
  } catch {
    // QuotaExceededError or storage unavailable
    // Intentionally silent: in-memory cache will still work for cached fields this session.
    return false;
  }
};

/**
 * Hydrate cache from sessionStorage on first access
 * Only reads from storage once per page load
 */
const hydrateCache = (): void => {
  if (cache.hydrated) return;

  cache.sessionId = safeStorageGet(SESSION_ID_KEY);
  const lastTouchRaw = safeStorageGet(SESSION_LAST_TOUCH_KEY);
  cache.lastTouchMs = lastTouchRaw ? Number(lastTouchRaw) : null;

  const attrRaw = safeStorageGet(ATTRIBUTION_KEY);
  if (attrRaw) {
    try {
      cache.attribution = JSON.parse(attrRaw) as Attribution;
    } catch (err) {
      if (typeof console !== 'undefined')
        console.warn('[session] Failed to parse cached attribution, resetting:', err);
      cache.attribution = {};
    }
  }

  cache.hydrated = true;
};

const safeRandomId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `s_${Math.random().toString(16).slice(2)}_${nowMs()}`;
};

export const getOrCreateSessionId = (): string => {
  hydrateCache();

  const isExpired = cache.lastTouchMs !== null && nowMs() - cache.lastTouchMs > SESSION_TIMEOUT_MS;

  if (!cache.sessionId || isExpired) {
    const next = safeRandomId();
    cache.sessionId = next;
    cache.lastTouchMs = nowMs();
    // Write-through to storage (non-blocking for functionality)
    safeStorageSet(SESSION_ID_KEY, next);
    safeStorageSet(SESSION_LAST_TOUCH_KEY, String(cache.lastTouchMs));
    return next;
  }

  // Touch the session
  cache.lastTouchMs = nowMs();
  safeStorageSet(SESSION_LAST_TOUCH_KEY, String(cache.lastTouchMs));
  return cache.sessionId;
};

export const touchSession = (): void => {
  hydrateCache();
  cache.lastTouchMs = nowMs();
  safeStorageSet(SESSION_LAST_TOUCH_KEY, String(cache.lastTouchMs));
};

const parseAttributionFromUrl = (url: string): Attribution => {
  try {
    const parsed = new URL(url);
    const params = parsed.searchParams;
    const attribution: Attribution = {
      utm_source: params.get('utm_source') ?? undefined,
      utm_medium: params.get('utm_medium') ?? undefined,
      utm_campaign: params.get('utm_campaign') ?? undefined,
      utm_content: params.get('utm_content') ?? undefined,
      utm_term: params.get('utm_term') ?? undefined,
      ref: params.get('ref') ?? undefined,
      source: params.get('source') ?? undefined,
      ref_user: params.get('ref_user') ?? undefined,
      ref_event: params.get('ref_event') ?? undefined,
      ref_question: params.get('ref_question') ?? undefined,
      ref_profile: params.get('ref_profile') ?? undefined,
    };

    // Remove empty strings
    return Object.fromEntries(
      Object.entries(attribution).filter(([, value]) => Boolean(value)),
    ) as Attribution;
  } catch (err) {
    if (typeof console !== 'undefined')
      console.warn('[session] Failed to parse attribution URL:', url, err);
    return {};
  }
};

export const getAttribution = (): Attribution => {
  hydrateCache();
  return cache.attribution ?? {};
};

export const captureAttribution = (url: string): { attribution: Attribution; isNew: boolean } => {
  hydrateCache();

  const next = parseAttributionFromUrl(url);
  const prev = cache.attribution ?? {};
  const merged = { ...prev, ...next };

  const isNew = JSON.stringify(prev) !== JSON.stringify(merged);

  // Update cache and persist
  cache.attribution = merged;
  safeStorageSet(ATTRIBUTION_KEY, JSON.stringify(merged));

  return { attribution: merged, isNew };
};

export const wasQrScanCapturedThisSession = (): boolean => qrScanTracked.read() === true;

export const markQrScanCapturedThisSession = (): void => {
  qrScanTracked.write(true);
};

export const wasReferralCapturedThisSession = (): boolean => referralTracked.read() === true;

export const markReferralCapturedThisSession = (): void => {
  referralTracked.write(true);
};

/**
 * Check if the attribution indicates app share traffic
 * Matches both generic "app_share" and platform-specific sources like "app_share_whatsapp"
 */
export const isAppShareTraffic = (attribution: Attribution): boolean => {
  return (
    attribution.source === 'app_share' || attribution.source?.startsWith('app_share_') === true
  );
};

/**
 * Extract the platform from an app_share source
 * e.g., "app_share_whatsapp" -> "whatsapp", "app_share" -> null
 */
export const getAppSharePlatform = (attribution: Attribution): string | null => {
  if (!attribution.source?.startsWith('app_share_')) return null;
  return attribution.source.slice('app_share_'.length);
};

export const wasAppShareCapturedThisSession = (): boolean => appShareTracked.read() === true;

export const markAppShareCapturedThisSession = (): void => {
  appShareTracked.write(true);
};

/**
 * Check if the attribution indicates QR code traffic
 * Matches: utm_source=qr, utm_medium=qr, utm_campaign=qr, or source=qr_[anything]
 */
export const isQrTraffic = (attribution: Attribution): boolean => {
  // Check UTM parameters
  if (
    attribution.utm_source === 'qr' ||
    attribution.utm_medium === 'qr' ||
    attribution.utm_campaign === 'qr'
  ) {
    return true;
  }

  // Check source=qr_[number] pattern (e.g., source=qr_123, source=qr_event_456)
  if (attribution.source?.startsWith('qr_')) {
    return true;
  }

  return false;
};

/**
 * Extract the QR code identifier from source parameter
 * e.g., "qr_123" -> "123", "qr_event_456" -> "event_456"
 */
export const getQrCodeId = (attribution: Attribution): string | null => {
  if (attribution.source?.startsWith('qr_')) {
    return attribution.source.slice(3); // Remove "qr_" prefix
  }
  return null;
};

// =============================================================================
// SESSION_STARTED DEDUPLICATION
// =============================================================================

/**
 * Get the session ID for which session_started was already tracked
 * Returns null if not yet tracked in this session
 *
 * AIDEV-NOTE: Uses sessionStorage (not useRef) to survive React StrictMode
 * double-mounting and HMR remounts in development. If storage refuses the write, the
 * mark is kept in storedValue's in-memory mirror so the dedupe still holds for this page.
 */
export const getSessionStartedTrackedId = (): string | null => sessionStartedTrackedId.read();

/**
 * Mark session_started as tracked for the given session ID
 */
export const markSessionStartedTracked = (sessionId: string): void => {
  sessionStartedTrackedId.write(sessionId);
};
